import type { CustomerRestrictionInput, CustomerRestrictionView } from '@chukua/common';
import { CUSTOMER_RESTRICTION_LABELS } from '@chukua/common';
import { Alert, Checkbox, Form, InputNumber, Modal } from 'antd';
import { useEffect } from 'react';

interface FormValues {
  years: number;
  isAdvisory: boolean;
}

interface CustomerRestrictionModalProps {
  open: boolean;
  /** The restriction being edited, or null when adding. */
  restriction: CustomerRestrictionView | null;
  submitting: boolean;
  error: string | null;
  onSubmit: (input: CustomerRestrictionInput) => Promise<void>;
  onCancel: () => void;
}

/** Maximum truck age is the only kind so far; a new kind adds its fields here and its entry in `CUSTOMER_RESTRICTION_KINDS`. */
export function CustomerRestrictionModal({ open, restriction, submitting, error, onSubmit, onCancel }: CustomerRestrictionModalProps) {
  const [form] = Form.useForm<FormValues>();

  useEffect(() => {
    if (open) form.setFieldsValue({ years: restriction?.params.years, isAdvisory: restriction?.isAdvisory ?? false });
  }, [open, restriction, form]);

  async function submit() {
    const { years, isAdvisory } = await form.validateFields();
    await onSubmit({ kind: 'max_truck_age', params: { years }, isAdvisory });
  }

  return (
    <Modal
      open={open}
      title={restriction ? 'Edit restriction' : 'Add restriction'}
      onOk={submit}
      onCancel={onCancel}
      confirmLoading={submitting}
      destroyOnClose
      okText={restriction ? 'Save' : 'Add'}
    >
      {error && <Alert type="error" showIcon message={error} className="mb-4" />}
      <Form<FormValues> form={form} layout="vertical" requiredMark={false}>
        <Form.Item
          name="years"
          label={CUSTOMER_RESTRICTION_LABELS.max_truck_age}
          rules={[
            { required: true, message: 'Enter a number of years' },
            { type: 'number', min: 1, max: 60, message: 'Between 1 and 60 years' },
          ]}
        >
          <InputNumber className="w-full" min={1} max={60} precision={0} autoFocus />
        </Form.Item>
        <Form.Item name="isAdvisory" valuePropName="checked" extra="An advisory restriction warns the dispatcher instead of blocking the assignment.">
          <Checkbox>Advisory only</Checkbox>
        </Form.Item>
      </Form>
    </Modal>
  );
}
